/**
 * Kataloge pro Kampagne: Ausrüstung, Waffen, Rüstungen. Grundbestand nach dem
 * Spielerhandbuch 2014 (deutsch), frei anpassbar und per CSV austauschbar –
 * z. B. für Settings, die nicht klassische Fantasy sind.
 */
import { z } from 'zod';

export const CATALOG_KINDS = ['gear', 'weapon', 'armor'] as const;
export type CatalogKind = (typeof CATALOG_KINDS)[number];

export interface CatalogItem {
  name: string;
  /** Wert je Stück in Goldmünzen. */
  value?: number;
  /** Gewicht je Stück in kg. */
  weight?: number;
  /** Waffe: category, damage, damageType, properties. Rüstung: category, ac, strength, stealth. */
  data: Record<string, string | number | boolean>;
}

export interface CatalogItemDTO extends CatalogItem {
  id: string;
  kind: CatalogKind;
}

export interface CatalogField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'boolean';
  /** Spaltennamen, die beim CSV-Import erkannt werden (klein geschrieben). */
  aliases: string[];
  /** Liegt im Datenobjekt statt als eigene Spalte. */
  inData?: boolean;
  /** Vorschläge für Textfelder. */
  options?: string[];
}

const NAME: CatalogField = { key: 'name', label: 'Name', type: 'text', aliases: ['name', 'gegenstand', 'bezeichnung', 'item', 'waffe', 'weapon', 'rüstung', 'armor', 'armour'] };
const VALUE: CatalogField = { key: 'value', label: 'Wert (GM)', type: 'number', aliases: ['wert', 'preis', 'kosten', 'value', 'price', 'cost', 'gm', 'gp'] };
const WEIGHT: CatalogField = { key: 'weight', label: 'Gewicht (kg)', type: 'number', aliases: ['gewicht', 'weight', 'kg'] };

export const CATALOG_FIELDS: Record<CatalogKind, CatalogField[]> = {
  gear: [NAME, VALUE, WEIGHT, { key: 'notes', label: 'Notiz', type: 'text', inData: true, aliases: ['notiz', 'beschreibung', 'notes', 'description'] }],
  weapon: [
    NAME,
    {
      key: 'category',
      label: 'Kategorie',
      type: 'text',
      inData: true,
      aliases: ['kategorie', 'art', 'category', 'type'],
      options: ['Einfache Nahkampfwaffe', 'Einfache Fernkampfwaffe', 'Nahkampf-Kriegswaffe', 'Fernkampf-Kriegswaffe'],
    },
    { key: 'damage', label: 'Schaden', type: 'text', inData: true, aliases: ['schaden', 'damage', 'dmg'] },
    { key: 'damageType', label: 'Schadensart', type: 'text', inData: true, aliases: ['schadensart', 'schadenstyp', 'damage type', 'damagetype'], options: ['Hieb', 'Stich', 'Wucht'] },
    { key: 'properties', label: 'Eigenschaften', type: 'text', inData: true, aliases: ['eigenschaften', 'properties'] },
    VALUE,
    WEIGHT,
  ],
  armor: [
    NAME,
    { key: 'category', label: 'Kategorie', type: 'text', inData: true, aliases: ['kategorie', 'art', 'category', 'type'], options: ['Leichte Rüstung', 'Mittelschwere Rüstung', 'Schwere Rüstung', 'Schild'] },
    { key: 'ac', label: 'RK', type: 'text', inData: true, aliases: ['rk', 'rüstungsklasse', 'ac', 'armor class', 'armour class'] },
    { key: 'strength', label: 'Stärke', type: 'number', inData: true, aliases: ['stärke', 'str', 'strength', 'stä'] },
    { key: 'stealth', label: 'Nachteil Heimlichkeit', type: 'boolean', inData: true, aliases: ['heimlichkeit', 'nachteil heimlichkeit', 'stealth', 'stealth disadvantage'] },
    VALUE,
    WEIGHT,
  ],
};

export const CATALOG_LABELS: Record<CatalogKind, string> = { gear: 'Ausrüstung', weapon: 'Waffen', armor: 'Rüstungen' };

// ---------- Grundbestand ----------

const g = (name: string, value: number, weight?: number): CatalogItem => ({ name, value, ...(weight !== undefined ? { weight } : {}), data: {} });
const SM = 'Einfache Nahkampfwaffe';
const SR = 'Einfache Fernkampfwaffe';
const MM = 'Nahkampf-Kriegswaffe';
const MR = 'Fernkampf-Kriegswaffe';
const w = (name: string, category: string, value: number, damage: string, damageType: string, weight: number | undefined, properties = ''): CatalogItem => ({
  name,
  value,
  ...(weight !== undefined ? { weight } : {}),
  data: { category, damage, ...(damageType ? { damageType } : {}), ...(properties ? { properties } : {}) },
});
const a = (name: string, category: string, value: number, ac: string, weight: number, stealth = false, strength?: number): CatalogItem => ({
  name,
  value,
  weight,
  data: { category, ac, ...(stealth ? { stealth: true } : {}), ...(strength ? { strength } : {}) },
});

export const DEFAULT_CATALOG: Record<CatalogKind, readonly CatalogItem[]> = {
  gear: [
    g('Fackel', 0.01, 0.5),
    g('Rationen (1 Tag)', 0.5, 1),
    g('Wasserschlauch', 0.2, 2.5),
    g('Hanfseil (15 m)', 1, 5),
    g('Seidenseil (15 m)', 10, 2.5),
    g('Heiltrank', 50, 0.25),
    g('Rucksack', 2, 2.5),
    g('Schlafsack', 1, 3.5),
    g('Decke', 0.5, 1.5),
    g('Zunderkästchen', 0.5, 0.5),
    g('Kerze', 0.01),
    g('Laterne mit Haube', 5, 1),
    g('Öl (Flasche)', 0.1, 0.5),
    g('Brechstange', 2, 2.5),
    g('Kletterhaken', 0.05, 0.125),
    g('Kreide', 0.01),
    g('Stahlspiegel', 5, 0.25),
    g('Zelt (zwei Personen)', 2, 10),
    g('Diebeswerkzeug', 25, 0.5),
    g('Heilerausrüstung', 5, 1.5),
    g('Pergament (Blatt)', 0.1),
    g('Tinte (30 ml)', 10),
    g('Pfeile (20)', 1, 0.5),
    g('Armbrustbolzen (20)', 1, 0.75),
    g('Schleuderkugeln (20)', 0.04, 0.75),
  ],
  weapon: [
    w('Knüppel', SM, 0.1, '1W4', 'Wucht', 1, 'leicht'),
    w('Dolch', SM, 2, '1W4', 'Stich', 0.5, 'Finesse, leicht, Wurfwaffe (6/18 m)'),
    w('Zweihandknüppel', SM, 0.2, '1W8', 'Wucht', 5, 'zweihändig'),
    w('Beil', SM, 5, '1W6', 'Hieb', 1, 'leicht, Wurfwaffe (6/18 m)'),
    w('Wurfspeer', SM, 0.5, '1W6', 'Stich', 1, 'Wurfwaffe (9/36 m)'),
    w('Leichter Hammer', SM, 2, '1W4', 'Wucht', 1, 'leicht, Wurfwaffe (6/18 m)'),
    w('Streitkolben', SM, 5, '1W6', 'Wucht', 2),
    w('Kampfstab', SM, 0.2, '1W6', 'Wucht', 2, 'vielseitig (1W8)'),
    w('Sichel', SM, 1, '1W4', 'Hieb', 1, 'leicht'),
    w('Speer', SM, 1, '1W6', 'Stich', 1.5, 'Wurfwaffe (6/18 m), vielseitig (1W8)'),
    w('Leichte Armbrust', SR, 25, '1W8', 'Stich', 2.5, 'Geschosse (24/96 m), Laden, zweihändig'),
    w('Wurfpfeil', SR, 0.05, '1W4', 'Stich', 0.125, 'Finesse, Wurfwaffe (6/18 m)'),
    w('Kurzbogen', SR, 25, '1W6', 'Stich', 1, 'Geschosse (24/96 m), zweihändig'),
    w('Schleuder', SR, 0.1, '1W4', 'Wucht', undefined, 'Geschosse (9/36 m)'),
    w('Streitaxt', MM, 10, '1W8', 'Hieb', 2, 'vielseitig (1W10)'),
    w('Flegel', MM, 10, '1W8', 'Wucht', 1),
    w('Glefe', MM, 20, '1W10', 'Hieb', 3, 'schwer, Reichweite, zweihändig'),
    w('Zweihandaxt', MM, 30, '1W12', 'Hieb', 3.5, 'schwer, zweihändig'),
    w('Zweihandschwert', MM, 50, '2W6', 'Hieb', 3, 'schwer, zweihändig'),
    w('Hellebarde', MM, 20, '1W10', 'Hieb', 3, 'schwer, Reichweite, zweihändig'),
    w('Lanze', MM, 10, '1W12', 'Stich', 3, 'Reichweite, speziell'),
    w('Langschwert', MM, 15, '1W8', 'Hieb', 1.5, 'vielseitig (1W10)'),
    w('Zweihandhammer', MM, 10, '2W6', 'Wucht', 5, 'schwer, zweihändig'),
    w('Morgenstern', MM, 15, '1W8', 'Stich', 2),
    w('Pike', MM, 5, '1W10', 'Stich', 9, 'schwer, Reichweite, zweihändig'),
    w('Rapier', MM, 25, '1W8', 'Stich', 1, 'Finesse'),
    w('Krummsäbel', MM, 25, '1W6', 'Hieb', 1.5, 'Finesse, leicht'),
    w('Kurzschwert', MM, 10, '1W6', 'Stich', 1, 'Finesse, leicht'),
    w('Dreizack', MM, 5, '1W6', 'Stich', 2, 'Wurfwaffe (6/18 m), vielseitig (1W8)'),
    w('Kriegspicke', MM, 5, '1W8', 'Stich', 1),
    w('Kriegshammer', MM, 15, '1W8', 'Wucht', 1, 'vielseitig (1W10)'),
    w('Peitsche', MM, 2, '1W4', 'Hieb', 1.5, 'Finesse, Reichweite'),
    w('Blasrohr', MR, 10, '1', 'Stich', 0.5, 'Geschosse (7,5/30 m), Laden'),
    w('Handarmbrust', MR, 75, '1W6', 'Stich', 1.5, 'Geschosse (9/36 m), leicht, Laden'),
    w('Schwere Armbrust', MR, 50, '1W10', 'Stich', 9, 'Geschosse (30/120 m), schwer, Laden, zweihändig'),
    w('Langbogen', MR, 50, '1W8', 'Stich', 1, 'Geschosse (45/180 m), schwer, zweihändig'),
    w('Netz', MR, 1, '–', '', 1.5, 'speziell, Wurfwaffe (1,5/4,5 m)'),
  ],
  armor: [
    a('Gepolsterte Rüstung', 'Leichte Rüstung', 5, '11 + GES', 4, true),
    a('Lederrüstung', 'Leichte Rüstung', 10, '11 + GES', 5),
    a('Beschlagene Lederrüstung', 'Leichte Rüstung', 45, '12 + GES', 6.5),
    a('Fellrüstung', 'Mittelschwere Rüstung', 10, '12 + GES (max. 2)', 6),
    a('Kettenhemd', 'Mittelschwere Rüstung', 50, '13 + GES (max. 2)', 10),
    a('Schuppenpanzer', 'Mittelschwere Rüstung', 50, '14 + GES (max. 2)', 22.5, true),
    a('Brustplatte', 'Mittelschwere Rüstung', 400, '14 + GES (max. 2)', 10),
    a('Halbplattenrüstung', 'Mittelschwere Rüstung', 750, '15 + GES (max. 2)', 20, true),
    a('Ringpanzer', 'Schwere Rüstung', 30, '14', 20, true),
    a('Kettenpanzer', 'Schwere Rüstung', 75, '16', 27.5, true, 13),
    a('Schienenpanzer', 'Schwere Rüstung', 200, '17', 30, true, 15),
    a('Plattenpanzer', 'Schwere Rüstung', 1500, '18', 32.5, true, 15),
    a('Schild', 'Schild', 10, '+2', 3),
  ],
};

// ---------- Validierung ----------

const scalar = z.union([z.string().max(500), z.number(), z.boolean()]);
export const catalogItemSchema = z.object({
  name: z.string().trim().min(1).max(200),
  value: z.number().min(0).max(1e9).nullable().optional(),
  weight: z.number().min(0).max(1e6).nullable().optional(),
  data: z.record(z.string().max(50), scalar).default({}),
});
export const createCatalogItemSchema = catalogItemSchema.extend({ kind: z.enum(CATALOG_KINDS) });
export const updateCatalogItemSchema = catalogItemSchema.partial();
export const importCatalogSchema = z.object({
  kind: z.enum(CATALOG_KINDS),
  mode: z.enum(['merge', 'replace']),
  csv: z.string().max(5_000_000),
});

export interface CatalogImportResult {
  added: number;
  updated: number;
  removed: number;
  errors: string[];
}

// ---------- CSV ----------

/** Zerlegt CSV (Trennzeichen `;`, `,` oder Tab, automatisch erkannt; Anführungszeichen nach RFC 4180). */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, '');
  const firstLine = src.split(/\r?\n/, 1)[0] ?? '';
  const count = (ch: string) => firstLine.split(ch).length - 1;
  const sep = [';', '\t', ','].sort((x, y) => count(y) - count(x))[0]!;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === sep) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/** „1.250,5“ / „1250.5“ / „15 GM“ / „0,5 kg“ → Zahl. */
export function parseLocaleNumber(raw: string): number | undefined {
  let s = raw.trim().replace(/\s*(gm|gp|kg|lb)\.?$/i, '').replace(/\s/g, '');
  if (s === '' || s === '–' || s === '-') return undefined;
  if (s.includes(',')) s = s.replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  // „1.500“ ohne Komma: deutscher Tausenderpunkt („2.5“ bleibt 2,5)
  else if (/^[1-9]\d{0,2}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

const TRUE = new Set(['ja', 'x', 'true', 'yes', 'y', 'j', '1', 'nachteil', 'disadvantage', '✓']);

/** Liest CSV-Zeilen in Katalogeinträge; die Kopfzeile bestimmt die Spalten. */
export function csvToCatalog(kind: CatalogKind, text: string): { items: CatalogItem[]; errors: string[] } {
  const rows = parseCsv(text);
  const errors: string[] = [];
  if (rows.length === 0) return { items: [], errors: ['Die Datei ist leer.'] };
  const fields = CATALOG_FIELDS[kind];
  const header = rows[0]!.map((h) => h.trim().toLowerCase().replace(/\s*\(.*\)$/, ''));
  const colOf = new Map<string, number>();
  header.forEach((h, i) => {
    const f = fields.find((x) => x.aliases.includes(h) || x.key.toLowerCase() === h || x.label.toLowerCase().replace(/\s*\(.*\)$/, '') === h);
    if (f && !colOf.has(f.key)) colOf.set(f.key, i);
  });
  if (!colOf.has('name')) return { items: [], errors: ['Keine Spalte „Name“ gefunden. Erste Zeile muss die Spaltennamen enthalten.'] };
  const items: CatalogItem[] = [];
  rows.slice(1).forEach((r, idx) => {
    const line = idx + 2;
    const get = (key: string) => {
      const i = colOf.get(key);
      return i === undefined ? '' : (r[i] ?? '').trim();
    };
    const name = get('name');
    if (!name) return void errors.push(`Zeile ${line}: kein Name – übersprungen.`);
    const item: CatalogItem = { name: name.slice(0, 200), data: {} };
    for (const f of fields) {
      if (f.key === 'name' || !colOf.has(f.key)) continue;
      const raw = get(f.key);
      if (raw === '') continue;
      let v: string | number | boolean | undefined;
      if (f.type === 'number') {
        v = parseLocaleNumber(raw);
        if (v === undefined || v < 0) {
          errors.push(`Zeile ${line} („${name}“): ${f.label} „${raw}“ ist keine Zahl – ignoriert.`);
          continue;
        }
      } else if (f.type === 'boolean') v = TRUE.has(raw.toLowerCase());
      else v = raw.slice(0, 500);
      if (f.inData) item.data[f.key] = v;
      else if (f.key === 'value' || f.key === 'weight') item[f.key] = v as number;
    }
    items.push(item);
  });
  return { items, errors };
}

/** Katalog als CSV (Trennzeichen `;`, Dezimalkomma – öffnet sich direkt in deutschem Excel). */
export function catalogToCsv(kind: CatalogKind, items: readonly CatalogItem[]): string {
  const fields = CATALOG_FIELDS[kind];
  const esc = (s: string) => (/[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const cell = (f: CatalogField, it: CatalogItem): string => {
    const v = f.key === 'name' ? it.name : f.inData ? it.data[f.key] : it[f.key as 'value' | 'weight'];
    if (v === undefined || v === null || v === '') return '';
    if (typeof v === 'number') return String(v).replace('.', ',');
    if (typeof v === 'boolean') return v ? 'ja' : '';
    return esc(v);
  };
  return [fields.map((f) => esc(f.label)).join(';'), ...items.map((it) => fields.map((f) => cell(f, it)).join(';'))].join('\r\n') + '\r\n';
}

/** Kurzbeschreibung für Vorschlagslisten: „1W8 Hieb“ / „RK 11 + GES“. */
export function catalogDetail(kind: CatalogKind, item: Pick<CatalogItem, 'data'>): string {
  if (kind === 'weapon') return [item.data.damage, item.data.damageType].filter((x) => x !== undefined && x !== '').join(' ');
  if (kind === 'armor' && item.data.ac) return `RK ${String(item.data.ac)}`;
  return '';
}
